import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppShell from '../../components/layout/AppShell';
import {
  Eye, CheckCircle2, Stethoscope,
  HeartPulse, Scale, Thermometer, Wind, Activity, FlaskConical, Heart
} from 'lucide-react';
import { useChallengeApproach, useMyChallengeSubmissions } from '../../features/cases/hooks/use-challenges';

const isInvalidPlaceholder = (val) => {
  if (!val) return true;
  const lower = String(val).toLowerCase();
  return lower.includes('clinical essentials') || lower.includes('vet record') || lower.includes('bva member');
};

const DoctorApproachPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const attemptNumber = sessionStorage.getItem(`challenge_attempt_${id}`);
  const { data: approachData, isLoading, isError } = useChallengeApproach(id);
  const { data: submissionsData } = useMyChallengeSubmissions(id);

  if (isLoading) {
    return (
      <AppShell>
        <div className="flex min-h-screen justify-center items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-indigo"></div>
        </div>
      </AppShell>
    );
  }

  if (isError) {
    return (
      <AppShell>
        <div className="flex min-h-screen justify-center items-center flex-col gap-4 text-center px-4">
          <p className="text-gray-700 font-medium">You need to submit your response first before viewing the expert approach.</p>
          <button
            onClick={() => navigate(`/challenges/${id}/response`)}
            className="px-4 py-2 bg-[#7367f0] text-white rounded-lg text-sm"
          >
            Go to Response
          </button>
        </div>
      </AppShell>
    );
  }

  const approach = approachData?.data;
  const submissions = submissionsData?.data || [];

  const cleanSpecies = isInvalidPlaceholder(approach?.species) ? null : approach?.species;
  const cleanPatientInfo = isInvalidPlaceholder(approach?.patientInfo) ? null : approach?.patientInfo;

  const patientParts = [
    cleanSpecies,
    approach?.breed,
    approach?.ageValue ? `${approach.ageValue} ${approach.ageUnit || 'years'}` : null,
    approach?.sex,
    approach?.sterilization
  ].filter(Boolean);

  const formattedPatient = patientParts.length > 0
    ? patientParts.join(', ')
    : cleanPatientInfo;

  const hasAnyVitals = approach?.vitals && (
    approach.vitals.temperature ||
    approach.vitals.heartRate ||
    approach.vitals.respRate ||
    approach.vitals.weight ||
    approach.vitals.bcs ||
    approach.vitals.pain ||
    approach.vitals.crt ||
    approach.vitals.mm
  );

  // Determine treatment plan from plan / treatment / outcome
  const treatmentPlan = approach?.plan || approach?.treatment || approach?.outcome;

  return (
    <AppShell>
      {/* Header */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto py-6 px-4 flex flex-wrap justify-between items-center gap-4">
          <div>
            <button
              onClick={() => navigate(`/challenges/${id}/response`)}
              className="text-gray-500 hover:text-gray-900 flex items-center text-sm font-medium mb-3 transition-colors"
            >
              <span className="mr-1">{"<"}</span> Back to Response
            </button>
            <h1 className="text-2xl font-bold text-gray-900 mb-1">Doctor's Approach</h1>
            <p className="text-gray-500 text-sm">Review the expert clinical findings, diagnostics, and management</p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <span className="px-3 py-1 bg-green-50 text-green-600 rounded-full text-xs font-semibold border border-green-200 flex items-center gap-1 shadow-sm">
              <CheckCircle2 size={12} /> Response Submitted
            </span>
            {attemptNumber && (
              <span className="text-xs text-gray-400">Attempt #{attemptNumber}</span>
            )}
          </div>
        </div>
      </div>

      <div className="bg-gray-50/50 min-h-screen">
        <div className="max-w-4xl mx-auto py-8 px-4 pb-20 space-y-6">
          
          {/* Great Job Alert */}
          <div className="bg-emerald-50/70 rounded-xl border border-emerald-200 p-5">
            <h3 className="text-emerald-800 font-semibold mb-1">Great job submitting your clinical approach!</h3>
            <p className="text-emerald-700 text-sm">
              Review how the veterinarian conducted the physical examination, structured their diagnostic plan, and carried out treatment.
            </p>
          </div>

          {/* Patient Info & Vitals (Step 1 & Step 3 Vitals) */}
          {(formattedPatient || hasAnyVitals) && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-4">
              <div className="flex items-center gap-2">
                <Stethoscope className="text-blue-500" size={18} />
                <h3 className="text-sm font-bold text-gray-900">Patient Info & Vitals</h3>
              </div>

              {formattedPatient && (
                <div className="text-sm text-gray-700 bg-gray-50/80 px-3.5 py-2.5 rounded-lg border border-gray-100">
                  <span className="font-semibold text-gray-900">Patient Info: </span>{formattedPatient}
                </div>
              )}

              {/* Vitals Grid */}
              {hasAnyVitals && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-1">
                  {approach?.vitals?.temperature && (
                    <div className="bg-gray-50/80 p-3 rounded-lg border border-gray-100">
                      <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
                        <Thermometer size={14} className="text-red-500" /> Temp (°F)
                      </div>
                      <div className="text-sm font-bold text-gray-900">{approach.vitals.temperature}</div>
                    </div>
                  )}

                  {approach?.vitals?.heartRate && (
                    <div className="bg-gray-50/80 p-3 rounded-lg border border-gray-100">
                      <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
                        <HeartPulse size={14} className="text-pink-500" /> HR (/min)
                      </div>
                      <div className="text-sm font-bold text-gray-900">
                        {approach.vitals.heartRate}
                      </div>
                    </div>
                  )}

                  {approach?.vitals?.respRate && (
                    <div className="bg-gray-50/80 p-3 rounded-lg border border-gray-100">
                      <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
                        <Wind size={14} className="text-sky-500" /> RR (/min)
                      </div>
                      <div className="text-sm font-bold text-gray-900">
                        {approach.vitals.respRate}
                      </div>
                    </div>
                  )}

                  {approach?.vitals?.weight && (
                    <div className="bg-gray-50/80 p-3 rounded-lg border border-gray-100">
                      <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
                        <Scale size={14} className="text-indigo-500" /> Wt (kg)
                      </div>
                      <div className="text-sm font-bold text-gray-900">
                        {approach.vitals.weight}
                      </div>
                    </div>
                  )}

                  {approach?.vitals?.bcs && (
                    <div className="bg-gray-50/80 p-3 rounded-lg border border-gray-100">
                      <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
                        <Activity size={14} className="text-emerald-500" /> BCS
                      </div>
                      <div className="text-sm font-bold text-gray-900">{approach.vitals.bcs}</div>
                    </div>
                  )}

                  {approach?.vitals?.pain && (
                    <div className="bg-gray-50/80 p-3 rounded-lg border border-gray-100">
                      <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
                        <Heart size={14} className="text-amber-500" /> Pain
                      </div>
                      <div className="text-sm font-bold text-gray-900">{approach.vitals.pain}</div>
                    </div>
                  )}

                  {approach?.vitals?.crt && (
                    <div className="bg-gray-50/80 p-3 rounded-lg border border-gray-100">
                      <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
                        <Activity size={14} className="text-purple-500" /> CRT(s)
                      </div>
                      <div className="text-sm font-bold text-gray-900">{approach.vitals.crt}</div>
                    </div>
                  )}

                  {approach?.vitals?.mm && (
                    <div className="bg-gray-50/80 p-3 rounded-lg border border-gray-100">
                      <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
                        <Activity size={14} className="text-rose-500" /> MM color
                      </div>
                      <div className="text-sm font-bold text-gray-900">{approach.vitals.mm}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Physical Examination Findings */}
          {approach?.physicalExam && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-3">
              <div className="flex items-center gap-2">
                <Activity className="text-teal-600" size={18} />
                <h3 className="text-sm font-bold text-gray-900">Physical Examination Findings</h3>
              </div>
              <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-line bg-gray-50/60 p-3.5 rounded-lg border border-gray-100">
                {approach.physicalExam}
              </p>
            </div>
          )}

          {/* Step 4: Diagnostics */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <FlaskConical className="text-indigo-600" size={18} />
              <h3 className="text-sm font-bold text-gray-900">Diagnostics</h3>
            </div>

            {approach?.differentialDiagnosis && (
              <div>
                <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Differential Diagnosis</div>
                <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-line bg-gray-50/60 p-3 rounded-lg border border-gray-100">
                  {approach.differentialDiagnosis}
                </p>
              </div>
            )}

            {(approach?.diagnosticsPlan || approach?.tests?.length > 0) && (
              <div>
                <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Diagnostics Plan</div>
                {approach?.tests?.length > 0 ? (
                  <ul className="list-disc pl-5 space-y-1.5 text-sm text-gray-800 bg-gray-50/60 p-3 rounded-lg border border-gray-100">
                    {approach.tests.map((t, idx) => <li key={idx}>{t}</li>)}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-line bg-gray-50/60 p-3 rounded-lg border border-gray-100">
                    {approach.diagnosticsPlan}
                  </p>
                )}
              </div>
            )}

            {approach?.diagnosticResults && (
              <div>
                <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Diagnostic Results</div>
                <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-line bg-gray-50/60 p-3 rounded-lg border border-gray-100">
                  {approach.diagnosticResults}
                </p>
              </div>
            )}

            {/* Final Diagnosis */}
            {(approach?.finalDiagnosis || approach?.diagnosis) && (
              <div className="bg-indigo-50/60 border border-indigo-200 rounded-xl p-4 mt-2">
                <div className="flex items-center gap-2 mb-1">
                  <Eye className="text-indigo-600" size={16} />
                  <span className="text-xs font-bold text-indigo-900 uppercase tracking-wider">Final Diagnosis</span>
                </div>
                <p className="text-sm font-semibold text-indigo-950 leading-relaxed">
                  {approach.finalDiagnosis || approach.diagnosis}
                </p>
                {approach.prognosis && (
                  <div className="mt-2 text-xs text-indigo-800">
                    <span className="font-semibold">Prognosis: </span>{approach.prognosis}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Step 5: Treatment Plan */}
          {treatmentPlan && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-3">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="text-emerald-600" size={18} />
                <h3 className="text-sm font-bold text-gray-900">Treatment Plan</h3>
              </div>
              <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-line bg-gray-50/60 p-3.5 rounded-lg border border-gray-100">
                {treatmentPlan}
              </p>
            </div>
          )}

          {/* Past attempts notice */}
          {submissions.length > 1 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
              You have <strong>{submissions.length} attempts</strong> on this challenge. You can compare all your attempts in the comparison view.
            </div>
          )}

          {/* Action Buttons */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
            <button
              onClick={() => navigate(`/challenges/${id}/comparison`)}
              className="bg-[#7367f0] hover:bg-[#5e50ee] text-white py-3 rounded-lg font-medium flex justify-center items-center gap-2 transition-colors text-sm shadow-sm"
            >
              <Eye size={16} /> Compare Approaches
            </button>
            <button
              onClick={() => navigate('/challenges')}
              className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 py-3 rounded-lg font-medium transition-colors text-sm shadow-sm"
            >
              Complete Challenge
            </button>
          </div>

        </div>
      </div>
    </AppShell>
  );
};

export default DoctorApproachPage;
